import { useEffect, useState } from 'react';

import { useTracking } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import {
  dispatchCloseAnonReviewFormModal,
  dispatchOpenAnonReviewFormModal,
} from 'store/modals/actions';
import { daysAgo } from 'utils';
import { COMPANY_REVIEW_MOD_INTERVAL, PAGE_CATEGORY, REVIEW_FORM_LS_KEY } from 'utils/constant';

import type { CompanyAnonReviewFormProps } from '../companyAnonReviewForm';
import CompanyAnonReviewFormPopup from '../companyAnonReviewFormPopup';

interface Props extends CompanyAnonReviewFormProps {
  hasApprovedReviews?: boolean;
}

const TRACKING_HASHES = {
  DISPLAYED: '#reviews-popup-displayed',
  CLICKED: '#reviews-popup-clicked',
};

const isCompanyClusterPage = (pageCategory: string) =>
  [
    PAGE_CATEGORY.companyPage,
    PAGE_CATEGORY.companyBenefits,
    PAGE_CATEGORY.companyBenefitsPage,
    PAGE_CATEGORY.companyCompetitorsPage,
    PAGE_CATEGORY.companyDemographics,
    PAGE_CATEGORY.companyExecutivesPage,
    PAGE_CATEGORY.companySalaryPage,
    PAGE_CATEGORY.companyRevenuePage,
    PAGE_CATEGORY.companyReviewsPage,
    PAGE_CATEGORY.companyMissionStatementPage,
    PAGE_CATEGORY.companyFaqPage,
    PAGE_CATEGORY.companyLocationsPage,
    PAGE_CATEGORY.companyHistoryPage,
    // Dynamic pages:
    PAGE_CATEGORY.companyInterviewQuestions,
    PAGE_CATEGORY.companyDrugTests,
    PAGE_CATEGORY.companyBenefits,
    PAGE_CATEGORY.companyDressCode,
  ].some((category) => new RegExp(`${category}`, 'i').test(pageCategory));

/**
 * Variant of the CompanyAnonReviewFormPopup that controls its open state and contains logic
 * for opening globally.
 * This component is a layout of the ModalContainer and is not meant to be reused.
 */
const CompanyAnonReviewFormModal = (props: Props) => {
  const { hasApprovedReviews, ...anonReviewFormProps } = props;

  const modals = useAppSelector(({ modals }) => modals);
  const dispatch = useAppDispatch();
  const tracking = useTracking();

  const [companyClusterTimer, setCompanyClusterTimer] = useState(false);
  const [companyClusterScroll, setCompanyClusterScroll] = useState(false);

  const handleRatingChange = () => {
    window.location.hash = TRACKING_HASHES.CLICKED;
  };

  // Company Cluster popup conditions.
  useEffect(() => {
    if (!isCompanyClusterPage(window.pageCategory)) {
      return;
    }

    const popupShown = localStorage.getItem(REVIEW_FORM_LS_KEY.POPUP_SHOWN);
    const recentReviews = JSON.parse(localStorage.getItem(REVIEW_FORM_LS_KEY.RECENT_REVIEWS));
    const pageLastSeen = localStorage.getItem(REVIEW_FORM_LS_KEY.PAGE_LAST_SEEN);

    // Scroll condition.
    const scrollListener = () => {
      // True when the user scrolled down at least 30% of the page.
      if (window.scrollY > window.document.body.scrollHeight * 0.3) {
        setCompanyClusterScroll(true);
      }
    };
    window.addEventListener('scroll', scrollListener);

    // Timer condition.
    const timer = companyClusterTimer
      ? null
      : setTimeout(() => {
          setCompanyClusterTimer(true);
        }, 20000);

    // User saw popup in this page from other sources condition.
    const seenFromOtherSource = pageLastSeen?.toLowerCase() === window.pageCategory?.toLowerCase();

    // User saw popup before condition.
    const isFirstTime = popupShown === null || daysAgo(popupShown) >= 1;

    // User reviewed this company recently condition.
    const hasRecentReview = (() => {
      const thisCompany = recentReviews?.find(
        (entry) => entry.companyID === anonReviewFormProps.companyID
      );
      if (!thisCompany) return false;
      return daysAgo(thisCompany.time) <= COMPANY_REVIEW_MOD_INTERVAL;
    })();

    if (
      hasApprovedReviews === false &&
      (companyClusterScroll || companyClusterTimer) &&
      isFirstTime &&
      !seenFromOtherSource &&
      !hasRecentReview
    ) {
      dispatch(dispatchOpenAnonReviewFormModal());
      // Save date so we can ignore it after some cooldown interval.
      localStorage.setItem(REVIEW_FORM_LS_KEY.POPUP_SHOWN, new Date().toISOString());
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', scrollListener);
    };
  }, [companyClusterTimer, companyClusterScroll]);

  // Add/remove URL tracking when the modal is opened/closed.
  useEffect(() => {
    if (modals.anonReviewFormModal.open) {
      window.location.hash = TRACKING_HASHES.DISPLAYED;
    } else {
      window.location.hash = '';
    }
  }, [modals.anonReviewFormModal.open]);

  // Add a listener for hash changes to log HTTP_ACTIVITY events.
  useEffect(() => {
    const onHashChange = async () => {
      // Filter out unrelated hash changes.
      if (!Object.values(TRACKING_HASHES).includes(window.location.hash)) {
        return;
      }
      await tracking.logActivity();
    };

    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  // Expire localStorage keys.
  useEffect(() => {
    const popupShown = localStorage.getItem(REVIEW_FORM_LS_KEY.POPUP_SHOWN);
    const recentReviews = JSON.parse(localStorage.getItem(REVIEW_FORM_LS_KEY.RECENT_REVIEWS));
    const pageLastSeen = localStorage.getItem(REVIEW_FORM_LS_KEY.PAGE_LAST_SEEN);

    if (popupShown && daysAgo(popupShown) > COMPANY_REVIEW_MOD_INTERVAL) {
      localStorage.removeItem(REVIEW_FORM_LS_KEY.POPUP_SHOWN);
    }

    if (recentReviews) {
      const actuallyRecentReviews = recentReviews.filter(
        (review) => daysAgo(review.time) <= COMPANY_REVIEW_MOD_INTERVAL
      );
      localStorage.setItem(
        REVIEW_FORM_LS_KEY.RECENT_REVIEWS,
        JSON.stringify(actuallyRecentReviews)
      );
    }

    if (pageLastSeen?.toLowerCase() !== window.pageCategory?.toLowerCase()) {
      localStorage.removeItem(REVIEW_FORM_LS_KEY.PAGE_LAST_SEEN);
    }
  }, []);

  return (
    <CompanyAnonReviewFormPopup
      isGlobalModal
      shareText={
        <>
          {`Do you have experience working for ${anonReviewFormProps.companyName}?`}
          <br key="one" />
          Share your thoughts and help other job seekers.
        </>
      }
      onRatingChange={handleRatingChange}
      {...anonReviewFormProps}
      show={modals.anonReviewFormModal.open}
      onClose={() => dispatch(dispatchCloseAnonReviewFormModal())}
    />
  );
};

export default CompanyAnonReviewFormModal;
export { isCompanyClusterPage as isAllowedPage };
